import { defineStore } from "pinia";
import type { Pagination } from "@/types.ts";
import api from "@/_helpers/api.ts";
import { handleVirtualScrollData } from "@coronet/cui";
import { downloadFile } from "@/_helpers/download.ts";
import { axiosInstance } from "@/plugins/https.ts";
import { StoreModule } from "@/constants/store.ts";
import {
  FilterContext,
  type SecureWebGatewayFilters,
  useFiltersStore,
} from "@/_store/filters.module.ts";
import { SecureWebGatewayListFilterChoices } from "@/constants/network.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";
import type { DeviceLabel } from "@/_store/devices-settings.module.ts";
export interface SecureWebGatewaySettings {
  dnsAnonymousModeEnabled: boolean;
  dnsAllowlistFiltering: boolean;
  dnsFilteringEnabled: boolean;
  dnsBasicFilteringEnabled: boolean;
  localDnsEnabled: boolean;
  labels: DeviceLabel[];
  allowOnlyLabels: DeviceLabel[];
  isBasicMode: boolean;
  conditionalForwardingEnabled: boolean;
}

export interface SecureWebGatewayUrlItem {
  id: string;
  category: string;
  disabled: boolean;
  allowed: boolean;
  name: string;
  labels: DeviceLabel[];
}

export interface SecureWebGatewayAllowOnlyUrlItem {
  id: string;
  name: string;
  dateAdded: string;
}

export interface SecureWebGatewayState {
  settings: SecureWebGatewaySettings;
  total: number;
  urlList: Array<SecureWebGatewayUrlItem>;
  urlListLoading: boolean;
  configurationLoading: boolean;
  showSkeletonLoader: boolean;
  pagination: Pagination;
}

const defaultSecureWebGatewayState: SecureWebGatewayState = {
  settings: {
    dnsAnonymousModeEnabled: false,
    dnsFilteringEnabled: false,
    dnsBasicFilteringEnabled: false,
    localDnsEnabled: false,
    dnsAllowlistFiltering: false,
    labels: [],
    allowOnlyLabels: [],
    isBasicMode: true,
    conditionalForwardingEnabled: false,
  },
  total: 0,
  urlList: [],
  urlListLoading: false,
  configurationLoading: false,
  showSkeletonLoader: false,
  pagination: {
    page: 0,
    pageSize: 25,
  },
};

const convertFiltersForBackend = (filters: SecureWebGatewayFilters) => {
  const allowedFilterMap = {
    [SecureWebGatewayListFilterChoices.ALLOWED]: { allowed: true, disabled: false },
    [SecureWebGatewayListFilterChoices.BLOCKED]: { allowed: false, disabled: false },
    [SecureWebGatewayListFilterChoices.ALLOWED_DISABLED]: { allowed: true, disabled: true },
    [SecureWebGatewayListFilterChoices.BLOCKED_DISABLED]: { allowed: false, disabled: true },
  };

  const { allowed, disabled } = filters.allowed
    ? allowedFilterMap[filters.allowed]
    : { allowed: undefined, disabled: undefined };

  return {
    ...filters,
    allowed,
    disabled,
  };
};

const convertPayloadForBE = ({
  allowed,
  nickname,
  file,
  labels,
}: {
  allowed: boolean;
  nickname: string;
  file: File;
  labels: string;
}) => {
  const formData = new FormData();
  formData.append("allowed", allowed.toString());
  formData.append("name", nickname);
  formData.append("file", file);
  formData.append("labels", labels);
  return formData;
};
const SWG_TIMEOUT = 60000;

export const useSWGStore = defineStore(StoreModule.SECURE_WEB_GATEWAY, {
  state: (): SecureWebGatewayState => ({ ...defaultSecureWebGatewayState }),
  actions: {
    async getSwgUrlList(virtualScroll = false) {
      const filters = useFiltersStore().filters[FilterContext.SECURE_WEB_GATEWAY];
      const convertedFilters = convertFiltersForBackend(filters);

      const requestConfig = {
        ...api.swgUrlList(),
        method: "GET",
        params: {
          ...this.pagination,
          ...convertedFilters,
        },
      };

      try {
        this.urlListLoading = true;
        const {
          data: { items, total },
        } = await axiosInstance.request(requestConfig);
        this.urlList = virtualScroll ? handleVirtualScrollData(this.urlList, items, "id") : items;
        this.total = total;
      } catch (e) {
        console.error(e);
      } finally {
        this.urlListLoading = false;
      }
    },
    async addToSwgUrlList({
      allowed,
      names,
      labels,
    }: {
      allowed: boolean;
      names: string[];
      labels: DeviceLabel[];
    }) {
      const requestConfig = {
        ...api.swgUrlList(),
        method: "POST",
        data: { allowed, names, labels },
      };
      try {
        this.urlListLoading = true;
        const {
          data: { processed: quantity = 0 },
        } = await axiosInstance.request({ ...requestConfig, timeout: SWG_TIMEOUT });
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(
            `snackbar.messages.secureWebGateway.${
              allowed ? "addUrlToAllowlist" : "addUrlToBlocklist"
            }`,
            { quantity },
            quantity
          )
        );
        await this.getSwgUrlList();
      } catch (e) {
        console.error(e);
      } finally {
        this.urlListLoading = false;
      }
    },

    async importToSwgUrlList(payload: {
      nickname: string;
      file: File;
      allowed: boolean;
      labels: string;
    }) {
      const convertedPayload = convertPayloadForBE(payload);
      const requestConfig = api.importToSwgUrlList(convertedPayload);

      try {
        this.urlListLoading = true;
        const response = await axiosInstance.request(requestConfig);
        const {
          data: { processed: quantity = 0 },
        } = response;
        const { nickname, allowed } = payload;
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(
            `snackbar.messages.secureWebGateway.${
              allowed ? "importUrlGroupToAllowlist" : "importUrlGroupToBlocklist"
            }`,
            { nickname, quantity },
            quantity
          )
        );
        await this.getSwgUrlList();
        return {
          data: {
            successfullyUploaded: quantity,
            invalidRecords: response.data.total - quantity,
          },
        };
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        this.urlListLoading = false;
      }
    },

    async checkSwgUrlGroupNameUnique(name: string): Promise<boolean> {
      try {
        await axiosInstance.request(api.swgUrlsFileValidateUnique(name));
        return true;
      } catch {
        return false;
      }
    },

    async checkSwgUrlsUnique(names: string[], allowed: boolean): Promise<boolean> {
      try {
        const { data } = await axiosInstance.request<Array<{ name: string; allowed: boolean }>>(
          api.swgUrlValidateUnique(names, allowed)
        );
        return !data?.length;
      } catch {
        return false;
      }
    },

    async getSwgExampleFile() {
      const requestConfig = api.getSwgExampleFile();
      try {
        const { data, headers } = await axiosInstance.request(requestConfig);
        const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
        const fileName = "SecureWebGatewayUrlsExample.txt";
        downloadFile(blob, fileName);
      } catch (e) {
        console.error(e);
      }
    },

    async viewUrlList(id: string) {
      const requestConfig = api.viewUrlList(id);
      return await axiosInstance.request(requestConfig);
    },

    async toggleDisabledSwgUrl({ disabled, id }: { id: string; disabled: boolean }) {
      const requestConfig = {
        ...api.swgUrlListItem(id),
        method: "PUT",
        params: {
          disabled,
        },
      };

      try {
        this.urlListLoading = true;
        this.configurationLoading = true;
        await axiosInstance.request({ ...requestConfig, timeout: SWG_TIMEOUT });
        await this.getSwgUrlList();
      } catch (e) {
        console.error(e);
      } finally {
        this.urlListLoading = false;
        this.configurationLoading = false;
      }
    },

    async updateSwgUrl(payload: { id: string; labels: DeviceLabel[] }) {
      const requestConfig = {
        ...api.swgUrlListItemLabels(payload.id),
        method: "PUT",
        data: {
          labels: payload.labels?.map((label) => label.id) || [],
        },
      };

      try {
        this.urlListLoading = true;
        await axiosInstance.request({ ...requestConfig, timeout: SWG_TIMEOUT });
        await this.getSwgUrlList();
      } catch (error) {
        console.error(error);
      } finally {
        this.urlListLoading = false;
      }
    },

    async updateSwgSettings(payload: SecureWebGatewaySettings) {
      const requestConfig = api.updateSwgSettings(payload);
      try {
        this.urlListLoading = true;
        this.configurationLoading = true;
        await axiosInstance.request({ ...requestConfig, timeout: SWG_TIMEOUT });
        await this.getSwgUrlList();
        await this.getSwgSettings();
      } catch (e) {
        console.error(e);
      } finally {
        this.urlListLoading = false;
        this.configurationLoading = false;
      }
    },

    async removeFromSwgUrlList(id: string): Promise<void> {
      const requestConfig = {
        ...api.swgUrlListItem(id),
        method: "DELETE",
      };
      try {
        this.urlListLoading = true;
        await axiosInstance.request({ ...requestConfig, timeout: SWG_TIMEOUT });
        await this.getSwgUrlList();
      } catch (e) {
        console.error(e);
      } finally {
        this.urlListLoading = false;
      }
    },

    async getSwgSettings(showSkeletonLoader = false) {
      if (showSkeletonLoader) this.showSkeletonLoader = true;

      try {
        const request = {
          ...api.getSwgSettings,
        };

        const { data } = await axiosInstance.request(request);

        this.settings = data;
      } catch (error) {
        console.error(error);
      } finally {
        if (showSkeletonLoader) this.showSkeletonLoader = false;
      }
    },
  },
});
